import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Formik } from 'formik';

import LoginForm from '@RHEnvironmental/pattern-library/dist/js/components/LoginForm';
import AlertBox from '@RHEnvironmental/pattern-library/dist/js/components/AlertBox';
import PageHeading from '@RHEnvironmental/pattern-library/dist/js/components/PageHeading';
import PageSection from '@RHEnvironmental/pattern-library/dist/js/components/PageSection';
import Button from '@RHEnvironmental/pattern-library/dist/js/components/Button';
import MainLayout from '../../modules/MainLayout';
import MainContent from '../../modules/MainContent';

import { isDeactivated, isLoggedIn } from '../../../services/auth';
import navigator from '../../../services/ui/navigator';
import { errorCodeReasons, signIn } from '../../../actions/signIn';
import { INSPECTION_LISTING } from '../../../constants/routeDestinations';
import { setAlert } from '../../../actions/alerts';
import { ACCOUNT_DEACTIVATED } from '../../../constants/authErrorCodes';
import {sendToHome} from "../../../services/routing/sendToHome";
import Header from '../../modules/Header/Header';
import { Heading } from '@chakra-ui/react';

const SignIn = () => {
    const dispatch = useDispatch();
    const history = useHistory();

    const urlParams = new URLSearchParams(window.location.search);
    const returnedError = urlParams.get('error');

    const focusOnField = (inputId) => {
        document.getElementById(inputId).focus();
    };

    const showDeactivatedWarning =
        isDeactivated() || returnedError === ACCOUNT_DEACTIVATED;

    useEffect(() => {
        if (isLoggedIn() && !isDeactivated()) {
            sendToHome();
        }
        if (showDeactivatedWarning) {
            dispatch(setAlert(errorCodeReasons[ACCOUNT_DEACTIVATED], 'error'));
        }
    }, []);

    return (
        <MainLayout>
            <MainContent>
                <PageSection className="page-section--standalone">
                    <section className="signin-page flex flex-column">
                        <Heading as="h1" variant="blk-800-lt-3" className="mb-2">Sign in</Heading>
                        <Formik
                            initialValues={{ email: '', password: '' }}
                            onSubmit={async (
                                formValues,
                                { setSubmitting, setStatus }
                            ) => {
                                setSubmitting(true);
                                await dispatch(
                                    signIn(
                                        {
                                            email: formValues.email,
                                            password: formValues.password,
                                        },
                                        setStatus,
                                        location.state?.from
                                            ? location.state.from
                                            : ''
                                    )
                                );
                                setSubmitting(false);
                                history.push('/');
                            }}
                        >
                            {({ getFieldProps, handleSubmit, status }) => (
                                <>
                                    {status && status.errors && (
                                        <AlertBox
                                            className="rhe-login-page__error-box"
                                            theme="error"
                                        >
                                            <p className="mb-2">
                                                There was a problem signing you
                                                in.
                                            </p>
                                            <ul className="list--ul">
                                                {status.errorMessage && (
                                                    <li>
                                                        <Button
                                                            className="rhe-button__link"
                                                            onClick={() =>
                                                                focusOnField(
                                                                    'email'
                                                                )
                                                            }
                                                        >
                                                            {
                                                                status.errorMessage
                                                            }
                                                        </Button>
                                                    </li>
                                                )}
                                                {status.errors.map(
                                                    (error, i) => (
                                                        <li key={i}>
                                                            <Button
                                                                className="rhe-button__link"
                                                                onClick={() =>
                                                                    focusOnField(
                                                                        error.input
                                                                    )
                                                                }
                                                            >
                                                                {error.message}
                                                            </Button>
                                                        </li>
                                                    )
                                                )}
                                            </ul>
                                        </AlertBox>
                                    )}
                                    <LoginForm
                                        email={getFieldProps('email')}
                                        password={getFieldProps('password')}
                                        onSubmit={handleSubmit}
                                        submitButton={{ type: 'submit' }}
                                        forgottenPasswordLink={{
                                            onClick: () => {
                                                window.open(
                                                    window.config
                                                        .forgotten_password_link,
                                                    '_blank'
                                                );
                                            },
                                        }}
                                        showCreateAccountLink={false}
                                    />
                                </>
                            )}
                        </Formik>
                    </section>
                </PageSection>
            </MainContent>
        </MainLayout>
    );
};

export default SignIn;
